import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import BackOfficeConfig from './Config';
import noImage from './images/noImage.png';

function getStoredProfile() {
  try {
    return JSON.parse(localStorage.getItem('apiUser') || 'null')?.profile || null;
  } catch (_error) {
    return null;
  }
}

function resolveProfileImage(image) {
  if (!image) return noImage;
  if (/^(?:https?:|data:|blob:)/i.test(image)) return image;

  const baseUrl = BackOfficeConfig.BASE_API_URL || '';
  return `${baseUrl.replace(/\/$/, '')}/${String(image).replace(/^\//, '')}`;
}

function ProfileDetail({ label, value }) {
  return (
    <Box>
      <Typography
        component="dt"
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.04em' }}
      >
        {label}
      </Typography>
      <Typography component="dd" variant="body1" sx={{ m: 0, mt: 0.4, overflowWrap: 'anywhere' }}>
        {value || 'Not available'}
      </Typography>
    </Box>
  );
}

class Profile extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      profile: getStoredProfile(),
    };
  }

  componentDidMount() {
    if (this.props.onReady) this.props.onReady();
  }

  render() {
    const profile = this.state.profile;

    if (!profile) {
      return (
        <Box sx={{ maxWidth: 880, mx: 'auto', textAlign: 'left' }}>
          <Alert severity="warning">Profile details are unavailable for this session.</Alert>
        </Box>
      );
    }

    const person = profile.person || {};
    const displayName = [profile.first_name, profile.last_name]
      .filter(Boolean)
      .join(' ')
      || [person.firstName, person.lastName].filter(Boolean).join(' ')
      || profile.username
      || 'User';
    const email = profile.email || person.email;
    const role = BackOfficeConfig.USER_TYPES?.[profile.user_type]
      || (profile.user_type !== undefined && profile.user_type !== null
        ? `User type ${profile.user_type}`
        : null);
    const status = BackOfficeConfig.USER_STATUSES_WITH_ICONS?.[profile.user_status]?.name
      || (profile.user_status !== undefined && profile.user_status !== null
        ? `Status ${profile.user_status}`
        : null);

    return (
      <Box sx={{ maxWidth: 880, mx: 'auto', textAlign: 'left' }}>
        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Avatar
                alt={displayName}
                src={resolveProfileImage(person.image)}
                sx={{ width: 72, height: 72 }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
                  {displayName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  My Profile · Read-only account details
                </Typography>
              </Box>
              {status ? <Chip label={status} color={profile.user_status === 1 ? 'success' : 'default'} /> : null}
            </Stack>
          </Box>

          <Divider />

          <Box
            component="dl"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 3,
              m: 0,
              p: { xs: 2, sm: 3 },
            }}
          >
            <ProfileDetail label="Username" value={profile.username} />
            <ProfileDetail label="Email" value={email} />
            <ProfileDetail label="Role" value={role} />
            <ProfileDetail label="Account status" value={status} />
          </Box>
        </Paper>
      </Box>
    );
  }
}

export default Profile;
